import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Search, Eye, Link as LinkIcon, X, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface BlogPost {
    id: number;
    blog_id?: number;
    title: string;
    slug: string;
    created_at: string;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const ParentsViewed: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    // Available blogs state
    const [availableBlogs, setAvailableBlogs] = useState<BlogPost[]>([]);
    const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50,
    });

    // Selected blogs state
    const [selectedBlogIds, setSelectedBlogIds] = useState<number[]>([]);
    const [mappedBlogs, setMappedBlogs] = useState<BlogPost[]>([]);
    const [morePavLink, setMorePavLink] = useState('');
    const [_isLoadingMappings, setIsLoadingMappings] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch available blogs
    const fetchAvailableBlogs = async (page: number = 1) => {
        try {
            setIsLoadingBlogs(true);
            const response = await apiRequest(`/school-blogs/available?page=${page}&limit=50&search=${encodeURIComponent(searchTerm)}`);

            if (response.success) {
                setAvailableBlogs(response.data.blogs || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch blogs', 'error');
            }
        } catch (error) {
            console.error('Error fetching blogs:', error);
            showToast('Failed to fetch blogs', 'error');
        } finally {
            setIsLoadingBlogs(false);
        }
    };

    // Fetch school parents-viewed blog mappings
    const fetchParentsViewedBlogs = async () => {
        try {
            setIsLoadingMappings(true);
            const response = await apiRequest(`/school-blogs/${id}/parents-viewed`);

            if (response.success) {
                const mapped = response.data.mappedBlogs || [];
                setMappedBlogs(mapped);
                setSelectedBlogIds(mapped.map((b: BlogPost) => b.blog_id));
                setMorePavLink(response.data.morePavLink || '');
            } else {
                showToast('Failed to fetch parents viewed blogs', 'error');
            }
        } catch (error) {
            console.error('Error fetching parents viewed blogs:', error);
            showToast('Failed to fetch parents viewed blogs', 'error');
        } finally {
            setIsLoadingMappings(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchAvailableBlogs(1);
        fetchParentsViewedBlogs();
    }, [id]);

    // Search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAvailableBlogs(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Handle blog selection
    const handleBlogToggle = (blogId: number) => {
        setSelectedBlogIds(prev => {
            if (prev.includes(blogId)) {
                return prev.filter(id => id !== blogId);
            } else {
                return [...prev, blogId];
            }
        });
    };

    // Handle remove from selected
    const handleRemoveSelected = (blogId: number) => {
        setSelectedBlogIds(prev => prev.filter(id => id !== blogId));
    };

    // Handle save
    const handleSave = async () => {
        try {
            setIsSaving(true);
            const response = await apiRequest(`/school-blogs/${id}/parents-viewed`, {
                method: 'POST',
                body: JSON.stringify({
                    blog_ids: selectedBlogIds,
                    more_pav_link: morePavLink,
                }),
            });

            if (response.success) {
                showToast('Parents viewed blogs saved successfully', 'success');
                fetchParentsViewedBlogs();
            } else {
                showToast('Failed to save blogs', 'error');
            }
        } catch (error) {
            console.error('Error saving blogs:', error);
            showToast('Failed to save blogs', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get selected blog details
    const getSelectedBlogDetails = () => {
        const allBlogs = [...mappedBlogs, ...availableBlogs];
        return selectedBlogIds.map(blogId => {
            const blog = allBlogs.find(b => b.id === blogId || b.blog_id === blogId);
            return blog || { id: blogId, title: `Blog #${blogId}`, slug: '', created_at: '' };
        });
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Eye className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Parents Also Viewed</h1>
                            <p className="text-xs text-gray-500">Attach blog articles parents also viewed</p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Available Blogs */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4">Available Blogs</h2>

                        {/* Search */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search blogs..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Blogs List */}
                        <div className="space-y-2 max-h-[320px] overflow-y-auto">
                            {isLoadingBlogs ? (
                                <p className="text-sm text-gray-500 text-center py-4">Loading blogs...</p>
                            ) : availableBlogs.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No blogs found</p>
                            ) : (
                                availableBlogs.map((blog) => {
                                    const isSelected = selectedBlogIds.includes(blog.id);
                                    return (
                                        <div
                                            key={blog.id}
                                            onClick={() => handleBlogToggle(blog.id)}
                                            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                                isSelected
                                                    ? 'bg-blue-50 border-blue-300'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                                                isSelected
                                                    ? 'bg-brandColor border-brandColor'
                                                    : 'border-gray-300'
                                            }`}>
                                                {isSelected && <Check size={12} className="text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{blog.title}</p>
                                                <p className="text-xs text-gray-500">{formatDate(blog.created_at)}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <span className="text-xs text-gray-500">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableBlogs(pagination.currentPage - 1)}
                                        disabled={pagination.currentPage === 1}
                                    >
                                        <ChevronLeft size={14} />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchAvailableBlogs(pagination.currentPage + 1)}
                                        disabled={pagination.currentPage === pagination.totalPages}
                                    >
                                        <ChevronRight size={14} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Selected Blogs */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            Selected Blogs ({selectedBlogIds.length})
                        </h2>

                        {/* More PAV Link */}
                        <div className="mb-4">
                            <label className="text-xs text-gray-500 uppercase mb-1 block">More PAV Link (Optional)</label>
                            <div className="relative">
                                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="url"
                                    value={morePavLink}
                                    onChange={(e) => setMorePavLink(e.target.value)}
                                    placeholder="https://example.com/parents-viewed"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Selected Blogs List */}
                        <div className="space-y-2 max-h-[320px] overflow-y-auto">
                            {selectedBlogIds.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No blogs selected</p>
                            ) : (
                                getSelectedBlogDetails().map((blog) => (
                                    <div
                                        key={blog.id || blog.blog_id}
                                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{blog.title}</p>
                                            {blog.created_at && (
                                                <p className="text-xs text-gray-500">{formatDate(blog.created_at)}</p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSelected(blog.id || blog.blog_id!)}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParentsViewed;
